import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import GroupIcon from '@mui/icons-material/Group';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import HandymanIcon from '@mui/icons-material/Handyman';
import SellIcon from '@mui/icons-material/Sell';
import KeyIcon from '@mui/icons-material/Key';
import { CardContent, Box, Typography } from '@mui/material';
import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { StyledCard, StyledCardMedia, PlaceholderIcon } from '@/components/ui/CardStyles.tsx';
import { investmentTypeLabels } from '@/features/investments/constants.ts';
import { getInvestmentCountryLabel } from '@/features/investments/countries.ts';
import { investmentStatusLabelMap } from '@/features/investments/util.ts';
import TooltipStatus, { TooltipStatusProps } from '../../components/ui/TooltipStatus.tsx';
import { formatCurrency, formatPercentage } from '@/utils/misc.ts';
import { InvestmentDTO, InvestmentStatus, InvestorListDTO } from '@inversor-hub/types';

const statusConfigMap: Record<
  InvestmentStatus,
  { label: string; tooltip: string; color: TooltipStatusProps['color']; icon: ReactNode }
> = {
  PURCHASING: {
    label: investmentStatusLabelMap['PURCHASING'],
    tooltip: 'La compra de la propiedad está en curso.',
    color: 'PENDING',
    icon: <BusinessCenterIcon fontSize="small" />,
  },
  RENOVATING: {
    label: investmentStatusLabelMap['RENOVATING'],
    tooltip: 'La propiedad está siendo reformada o adecuada.',
    color: 'PENDING',
    icon: <HandymanIcon fontSize="small" />,
  },
  LISTED_FOR_RENT_OR_SALE: {
    label: investmentStatusLabelMap['LISTED_FOR_RENT_OR_SALE'],
    tooltip: 'La propiedad está disponible para alquiler o venta.',
    color: 'MAIN',
    icon: <SellIcon fontSize="small" />,
  },
  RENTED: {
    label: investmentStatusLabelMap['RENTED'],
    tooltip: 'La propiedad ya está alquilada.',
    color: 'SUCCESS',
    icon: <KeyIcon fontSize="small" />,
  },
  SOLD: {
    label: investmentStatusLabelMap['SOLD'],
    tooltip: 'La propiedad ya está vendida.',
    color: 'SUCCESS',
    icon: <SellIcon fontSize="small" />,
  },
};

const getGrossReturnByType = (investment: InvestmentDTO): number | undefined => {
  switch (investment.type) {
    case 'TRADITIONAL_RENT':
      return investment.grossReturn;
    case 'ROOM_RENT':
      return investment.grossReturn;
    default:
      return undefined;
  }
};

export interface InvestmentCardProps {
  investment: InvestorListDTO;
  disabled?: boolean;
  onDisabledClick?: () => void;
}

export const InvestmentCard = ({
  investment,
  disabled = false,
  onDisabledClick,
}: InvestmentCardProps) => {
  const navigate = useNavigate();

  const handleCardClick = () => {
    if (disabled) {
      onDisabledClick?.();
      return;
    }

    navigate(`/inversiones/${investment.id}`);
  };

  const statusConfig = statusConfigMap[investment.status] || {
    label: 'Estado desconocido',
    tooltip: 'Desconocido',
    color: 'PENDING',
    icon: <BusinessCenterIcon fontSize="small" />,
  };

  const grossReturn = getGrossReturnByType(investment);

  return (
    <StyledCard
      onClick={handleCardClick}
      sx={
        disabled
          ? {
              cursor: onDisabledClick ? 'pointer' : 'not-allowed',
              opacity: 0.7,
              '&:hover': {
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                transform: 'none',
                borderColor: 'divider',
              },
            }
          : undefined
      }
    >
      <StyledCardMedia>
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 0.5,
            pointerEvents: 'none',
          }}
        >
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              color: 'info.contrastText',
              bgcolor: 'info.main',
              borderRadius: 2,
              fontSize: '0.75rem',
              fontWeight: 600,
              boxShadow: 1,
              whiteSpace: 'nowrap',
            }}
          >
            {investmentTypeLabels[investment.type]}
          </Box>
          {investment.isCoInvestment && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.5,
                bgcolor: 'secondary.main',
                color: 'common.white',
                borderRadius: 2,
                fontSize: '0.7rem',
                fontWeight: 600,
                backdropFilter: 'blur(4px)',
                whiteSpace: 'nowrap',
              }}
            >
              <GroupIcon sx={{ fontSize: 14 }} />
              Co-inversión
            </Box>
          )}
        </Box>

        {disabled ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 1,
              bgcolor: 'rgba(15, 23, 42, 0.45)',
              color: 'common.white',
              textAlign: 'center',
              p: 2,
            }}
          >
            <LockOutlinedIcon />
            <Typography variant="body2" fontWeight={700}>
              Disponible con un plan de pago
            </Typography>
            <Typography variant="caption">
              Solo puedes gestionar tu primera inversión con el plan gratuito.
            </Typography>
          </Box>
        ) : null}

        {investment.mainImageUrl ? (
          <img
            src={investment.mainImageUrl}
            alt={investment.alias || investment.address || 'Imagen de la inversión'}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <PlaceholderIcon />
        )}
      </StyledCardMedia>
      <CardContent sx={{ flexGrow: 1, p: 2 }}>
        <TooltipStatus
          label={statusConfig.label}
          tooltip={statusConfig.tooltip}
          color={statusConfig.color}
          icon={statusConfig.icon}
        />
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1, mb: 0.5 }}>
          {investment.alias || investment.address}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {investment.address}, {investment.city}
          {investment.country && investment.country !== 'ES'
            ? `, ${getInvestmentCountryLabel(investment.country)}`
            : ''}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Precio de compra:{' '}
          {investment.capex?.acquisitionPrice
            ? formatCurrency(investment.capex.acquisitionPrice)
            : '-'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Rentabilidad bruta: {grossReturn ? formatPercentage(grossReturn) : '-'}
        </Typography>
      </CardContent>
    </StyledCard>
  );
};

export default InvestmentCard;
